import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X, ChevronLeft, ChevronRight, Loader2, AlertCircle, Car } from 'lucide-react'
import { format, parseISO, addDays } from 'date-fns'
import { getRateCalendarDay, updateRateOverrides, type RateOverridePatch } from '../../lib/api/client'
import { cn } from '../../lib/cn'

const money = (c: unknown): string => {
  const n = Number(c)
  return Number.isFinite(n) ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n / 100) : '—'
}
const toEuros = (c: unknown): string => {
  const n = Number(c)
  return Number.isFinite(n) && n > 0 ? (n / 100).toFixed(2) : ''
}
const toCents = (e: string): number => {
  const n = parseFloat(String(e).replace(',', '.'))
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

type Draft = { special: string; enabled: boolean }

export function RateCalendarDayModal({
  date,
  onClose,
  onNavigate,
}: {
  date: string | null
  onClose: () => void
  onNavigate: (date: string) => void
}) {
  const qc = useQueryClient()
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const day = useQuery({
    queryKey: ['rateCalendarDay', date],
    queryFn: () => getRateCalendarDay(date!),
    enabled: Boolean(date),
  })

  useEffect(() => {
    if (!day.data) return
    const seed: Record<string, Draft> = {}
    for (const g of day.data) for (const r of g.rates) seed[r.id] = { special: toEuros(r.specialPrice), enabled: r.enabled }
    setDrafts(seed)
    setError(null)
  }, [day.data])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    if (date) document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [date, onClose])

  const allRates = useMemo(() => (day.data ?? []).flatMap((g) => g.rates), [day.data])

  if (!date) return null

  const setRow = (id: string, p: Partial<Draft>) => setDrafts((s) => ({ ...s, [id]: { ...s[id], ...p } }))

  async function save() {
    setError(null)
    setSaving(true)
    try {
      // Send every visible rate (matches the legacy dialog): a 0 special price clears the override.
      const rows: RateOverridePatch[] = allRates.map((r) => {
        const d = drafts[r.id] ?? { special: '', enabled: true }
        return { id: r.id, date: date!, price: toCents(d.special), enabled: d.enabled }
      })
      await updateRateOverrides(rows)
      await qc.invalidateQueries({ queryKey: ['rateCalendarGrid'] })
      await qc.invalidateQueries({ queryKey: ['rateCalendarDay'] })
      await qc.invalidateQueries({ queryKey: ['rates'] })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  const headDate = format(parseISO(date), 'EEEE dd/MM/yyyy')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-coal/40 backdrop-blur-[1px] animate-[fadeIn_.15s_ease]" onClick={onClose} />
      <div className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-card border border-line bg-paper shadow-pop">
        {/* header */}
        <header className="flex items-center gap-3 border-b border-line bg-card px-5 py-3.5">
          <div className="flex items-center gap-1">
            <button onClick={() => onNavigate(format(addDays(parseISO(date), -1), 'yyyy-MM-dd'))} className="rounded-md p-1 text-muted hover:bg-paper hover:text-coal" title="Giorno precedente">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[160px] text-center font-display text-[15px] font-bold capitalize text-coal">{headDate}</span>
            <button onClick={() => onNavigate(format(addDays(parseISO(date), 1), 'yyyy-MM-dd'))} className="rounded-md p-1 text-muted hover:bg-paper hover:text-coal" title="Giorno successivo">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button onClick={onClose} className="ml-auto rounded-lg p-1.5 text-muted hover:bg-paper hover:text-coal">
            <X className="h-[18px] w-[18px]" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {day.isLoading ? (
            <div className="flex items-center justify-center py-16 text-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : day.isError ? (
            <div className="py-16 text-center text-sm text-muted">
              <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare il giorno.
            </div>
          ) : (day.data?.length ?? 0) === 0 ? (
            <p className="py-14 text-center text-sm text-muted">Nessuna tariffa valida in questa data.</p>
          ) : (
            <div className="space-y-5">
              <p className="text-[12.5px] text-muted">
                Imposta un <b>prezzo speciale</b> per questo giorno. Lascia vuoto (o 0) per usare il listino normale.
              </p>
              {day.data!.map((g) => (
                <div key={g.label} className="overflow-hidden rounded-card border border-line">
                  <div className="flex items-center gap-2 border-b border-line-soft bg-card-2 px-4 py-2.5">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-paper text-slate ring-1 ring-line">
                      <Car className="h-3.5 w-3.5" />
                    </span>
                    <span className="font-display text-[14px] font-bold text-coal">{g.label}</span>
                  </div>
                  <table className="w-full text-[13px]">
                    <thead>
                      <tr className="text-[11px] font-bold uppercase tracking-[1px] text-muted">
                        <th className="px-4 py-2 text-left">Tariffa</th>
                        <th className="px-3 py-2 text-right">Listino</th>
                        <th className="px-3 py-2 text-right">Prezzo speciale</th>
                        <th className="px-4 py-2 text-right">Attivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.rates.map((r) => {
                        const d = drafts[r.id] ?? { special: '', enabled: true }
                        const special = toCents(d.special)
                        const diff = special > 0 && special !== r.price
                        return (
                          <tr key={r.id} className="border-t border-line-soft">
                            <td className="px-4 py-2.5 font-medium text-coal">{r.label}</td>
                            <td className="px-3 py-2.5 text-right font-mono tnum text-muted">{money(r.price)}</td>
                            <td className="px-3 py-2.5 text-right">
                              <div className="ml-auto flex h-8 w-28 items-center gap-1 rounded-md border border-line bg-card px-2 focus-within:border-verdigris">
                                <span className="text-[11px] text-muted">€</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={d.special}
                                  disabled={saving || !d.enabled}
                                  placeholder={(r.price / 100).toFixed(2)}
                                  onChange={(e) => setRow(r.id, { special: e.target.value })}
                                  className="w-full bg-transparent text-right font-mono text-[13px] text-coal outline-none placeholder:text-faint disabled:opacity-50"
                                />
                              </div>
                              {diff && (
                                <span className={cn('mt-0.5 block text-[11px] font-semibold', special < r.price ? 'text-ok' : 'text-venetian')}>
                                  {special < r.price ? '−' : '+'}
                                  {Math.abs(Math.round((1 - special / r.price) * 100))}%
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <input type="checkbox" checked={d.enabled} disabled={saving} onChange={(e) => setRow(r.id, { enabled: e.target.checked })} className="h-4 w-4 accent-verdigris" />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="flex items-center gap-3 border-t border-line bg-card px-5 py-3">
          {error ? (
            <span className="text-[12px] font-medium text-venetian">{error}</span>
          ) : (
            <span className="text-[11.5px] text-muted">Si applica solo a questa data, su tutte le durate.</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} disabled={saving} className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-50">
              Annulla
            </button>
            <button onClick={save} disabled={saving || (day.data?.length ?? 0) === 0} className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
